import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowLeft,
    ChatCircle,
    ChatsCircle,
    CheckCircle,
    ClockCounterClockwise,
    Globe,
    Microphone,
    MicrophoneSlash,
    PaperPlaneRight,
    Plus,
    Spinner,
    UsersThree,
    Warning,
    WifiHigh,
    WifiSlash,
    Paperclip,
    X,
    ShieldSlash,
    FlagPennant,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { messagesApi, postsApi, reelsApi } from "@/lib/apiClient";
import { useMessagingRealtime } from "@/components/messages/useMessagingRealtime";
import { MessageBubble } from "@/components/messages/MessageBubble";
import NewConversationModal from "@/components/messages/NewConversationModal";
import SharePassportModal from "@/components/messages/SharePassportModal";
import { ComposerAttachmentDrafts } from "@/components/messages/ComposerAttachmentDrafts";
import { GroupDetailsDrawer } from "@/components/messages/GroupDetailsDrawer";
import { ProtectedConversationAvatar } from "@/components/messages/ProtectedConversationAvatar";
import { ReportModal } from "@/components/PostCard";
import { MediaLightboxModal } from "@/components/messages/MediaLightboxModal";
import ReelModal from "@/components/ReelModal";
import CommentsDrawer from "@/components/CommentsDrawer";
import SharedPassportDetailModal from "@/components/messages/SharedPassportDetailModal";

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_VIDEO_BYTES = 40 * 1024 * 1024;
const MAX_VOICE_BYTES = 10 * 1024 * 1024;
const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;
const DOCUMENT_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv", "json"];

function timeAgo(iso) {
    if (!iso) return "";
    const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
}

function upsertConversation(prev, incoming) {
    const next = [incoming, ...prev.filter((item) => item.id !== incoming.id)];
    return next.sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
}

function dedupeMessages(items) {
    const map = new Map();
    items.forEach((item) => map.set(item.id, item));
    return [...map.values()].sort((a, b) => (a.seq || 0) - (b.seq || 0));
}

export default function Messages({ standalone = false, initialComposeHandle = "", initialConvId = "" }) {
    const { user } = useAuth();
    const [tab, setTab] = useState("inbox");
    const [conversations, setConversations] = useState([]);
    const [requests, setRequests] = useState({ incoming: [], outgoing: [], incoming_count: 0, outgoing_count: 0 });
    const [groupInvites, setGroupInvites] = useState([]);
    const [savedItems, setSavedItems] = useState([]);
    const [showSaved, setShowSaved] = useState(false);
    const [activeConversation, setActiveConversation] = useState(null);
    const [activeRequest, setActiveRequest] = useState(null);
    const [messages, setMessages] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [search, setSearch] = useState("");
    const [loadingShell, setLoadingShell] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [sending, setSending] = useState(false);
    const [text, setText] = useState("");
    const [replyTo, setReplyTo] = useState(null);
    const [shareDraft, setShareDraft] = useState(null);
    const [sharePreviews, setSharePreviews] = useState({});
    const [savedMessageIds, setSavedMessageIds] = useState(new Set());
    const [showNewModal, setShowNewModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showGroupDrawer, setShowGroupDrawer] = useState(false);
    const [reportUserId, setReportUserId] = useState(null);
    const [threadNotice, setThreadNotice] = useState("");
    const [recording, setRecording] = useState(false);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const [recordingWaveform, setRecordingWaveform] = useState([]);
    const [attachmentDrafts, setAttachmentDrafts] = useState([]);
    const [typingUsers, setTypingUsers] = useState({});
    const [activeMediaPreview, setActiveMediaPreview] = useState(null);
    const [activeReelModal, setActiveReelModal] = useState(null);
    const [activeSharedDetail, setActiveSharedDetail] = useState(null);
    const [commentPost, setCommentPost] = useState(null);

    const fileRef = useRef(null);
    const textareaRef = useRef(null);
    const scrollRef = useRef(null);
    const typingTimerRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const mediaChunksRef = useRef([]);
    const intervalRef = useRef(null);
    const uploadControllersRef = useRef({});
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const recordingFrameRef = useRef(null);
    const recordingStreamRef = useRef(null);
    const discardRecordingRef = useRef(false);
    const recordingSecondsRef = useRef(0);
    const recordingWaveformRef = useRef([]);
    const composeHandledRef = useRef(false);
    const textRef = useRef(text);
    const replyToRef = useRef(replyTo);
    const shareDraftRef = useRef(shareDraft);
    const conversationDraftsRef = useRef({});
    const activeConversationIdRef = useRef(activeConversation?.id);

    const loadShell = useCallback(async () => {
        try {
            setLoadingShell(true);
            const [conversationData, requestData, invitesData, savedData] = await Promise.all([
                messagesApi.conversations({ limit: 50 }),
                messagesApi.requests(),
                messagesApi.groupInvites(),
                messagesApi.savedMessages({ limit: 30 }),
            ]);
            setConversations(conversationData.items || []);
            setRequests(requestData);
            setGroupInvites(invitesData || []);
            const saved = savedData.items || [];
            setSavedItems(saved);
            setSavedMessageIds(new Set(saved.map((item) => item.message?.id).filter(Boolean)));
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not load Passport messages.");
        } finally {
            setLoadingShell(false);
        }
    }, []);

    const ensureSharePreview = useCallback(async (shareRef) => {
        if (!shareRef?.entity_type || !shareRef?.entity_id) return null;
        const key = `${shareRef.entity_type}:${shareRef.entity_id}`;
        if (sharePreviews[key]) return sharePreviews[key];
        try {
            const preview = await messagesApi.sharedPreview(shareRef, { allow_unavailable: true });
            setSharePreviews((prev) => ({ ...prev, [key]: preview }));
            return preview;
        } catch (_) {
            return null;
        }
    }, [sharePreviews]);

    const handlePassportCardClick = useCallback((preview) => {
        if (!preview) return;
        const { entity_type, data } = preview;
        if (entity_type === "reel") {
            setActiveReelModal({
                ...data,
                id: preview.entity_id,
                media: data?.media?.length
                    ? data.media
                    : [{ url: preview.image_url, kind: "video" }],
                views: data?.views ?? 0,
                likes: data?.likes ?? 0,
                caption: data?.caption || preview.title,
                user_handle: data?.user_handle || preview.subtitle?.replace("@", ""),
                user_avatar: data?.user_avatar,
                user_name: data?.user_name,
                liked: Boolean(data?.liked),
                saved: Boolean(data?.saved),
            });
        } else if (entity_type === "post") {
            const isVideo = preview.image_url?.match(/\.(mp4|webm|mov)(\?.*)?$/i);
            setActiveReelModal({
                ...data,
                id: preview.entity_id,
                media: data?.media?.length
                    ? data.media
                    : preview.image_url
                    ? [{ url: preview.image_url, kind: isVideo ? "video" : "image" }]
                    : [],
                views: data?.views ?? 0,
                likes: data?.reactions_total || data?.likes || 0,
                comments_count: data?.comments || 0,
                caption: data?.caption || preview.title,
                user_handle: data?.user_handle || preview.subtitle?.replace("@", ""),
                user_avatar: data?.user_avatar,
                user_name: data?.user_name,
                liked: Boolean(data?.my_reaction),
                saved: Boolean(data?.saved),
                is_post: true,
            });
        } else {
            setActiveSharedDetail(preview);
        }
    }, []);

    const revokeDraftPreview = useCallback((draft) => {
        if (draft?.previewUrl?.startsWith?.("blob:")) {
            try { URL.revokeObjectURL(draft.previewUrl); } catch (_) {}
        }
    }, []);

    const clearAttachmentDrafts = useCallback(() => {
        setAttachmentDrafts((prev) => {
            prev.forEach((item) => revokeDraftPreview(item));
            return [];
        });
    }, [revokeDraftPreview]);

    useEffect(() => () => {
        clearAttachmentDrafts();
        if (recordingFrameRef.current) cancelAnimationFrame(recordingFrameRef.current);
        audioContextRef.current?.close?.().catch?.(() => {});
    }, [clearAttachmentDrafts]);

    const compressImageFile = useCallback(async (file) => {
        if (!file.type.startsWith("image/") || file.size < 1.5 * 1024 * 1024) return file;
        return new Promise((resolve) => {
            const image = new Image();
            const objectUrl = URL.createObjectURL(file);
            image.onload = () => {
                const scale = Math.min(1, 1600 / Math.max(image.width, image.height));
                const canvas = document.createElement("canvas");
                canvas.width = Math.max(1, Math.round(image.width * scale));
                canvas.height = Math.max(1, Math.round(image.height * scale));
                const ctx = canvas.getContext("2d");
                ctx?.drawImage(image, 0, 0, canvas.width, canvas.height);
                canvas.toBlob((blob) => {
                    URL.revokeObjectURL(objectUrl);
                    if (!blob || blob.size >= file.size) return resolve(file);
                    resolve(new File([blob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), { type: blob.type || "image/jpeg" }));
                }, file.type === "image/png" ? "image/png" : "image/jpeg", 0.82);
            };
            image.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                resolve(file);
            };
            image.src = objectUrl;
        });
    }, []);

    const describeFile = useCallback((file) => {
        const extension = (file.name.split(".").pop() || "").toLowerCase();
        if (file.type.startsWith("image/")) {
            if (file.size > MAX_IMAGE_BYTES) throw new Error("Images must be under 10MB.");
            return { kind: "image", acceptsPreview: true };
        }
        if (file.type.startsWith("video/")) {
            if (file.size > MAX_VIDEO_BYTES) throw new Error("Videos must be under 40MB.");
            return { kind: "video", acceptsPreview: true };
        }
        if (file.type.startsWith("audio/")) {
            if (file.size > MAX_VOICE_BYTES) throw new Error("Voice files must be under 10MB.");
            return { kind: "voice", acceptsPreview: false };
        }
        if (DOCUMENT_EXTENSIONS.includes(extension)) {
            if (file.size > MAX_DOCUMENT_BYTES) throw new Error("Files must be under 20MB.");
            return { kind: "document", acceptsPreview: false };
        }
        throw new Error("Unsupported file type. Use images, videos, audio, or common documents.");
    }, []);

    const addDraftFiles = useCallback(async (files) => {
        if (!activeConversation?.id || !files?.length) return;
        const nextDrafts = [];
        for (const file of files.slice(0, 10)) {
            try {
                const meta = describeFile(file);
                const prepared = meta.kind === "image" ? await compressImageFile(file) : file;
                nextDrafts.push({
                    id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    file: prepared,
                    name: prepared.name,
                    mime: prepared.type || file.type,
                    kind: meta.kind,
                    previewUrl: meta.acceptsPreview ? URL.createObjectURL(prepared) : null,
                    durationMs: meta.kind === "voice" ? recordingSeconds * 1000 : null,
                    waveform: meta.kind === "voice" ? [...recordingWaveform] : null,
                    status: "queued",
                    progress: 0,
                    serverAttachment: null,
                    error: null,
                });
            } catch (error) {
                toast.error(error.message || "Could not add that file.");
            }
        }
        setAttachmentDrafts((prev) => [...prev, ...nextDrafts].slice(0, 10));
        if (fileRef.current) fileRef.current.value = "";
    }, [activeConversation?.id, compressImageFile, describeFile, recordingSeconds, recordingWaveform]);

    const loadConversation = useCallback(async (conversationId, reset = true) => {
        if (!conversationId) return;
        try {
            setLoadingMessages(true);
            const [detail, page] = await Promise.all([
                messagesApi.conversation(conversationId),
                messagesApi.messages(conversationId, { limit: 30 }),
            ]);
            setActiveConversation(detail);
            setActiveRequest(null);
            setMessages(page.items || []);
            setNextCursor(page.next_cursor || null);
            (page.items || []).forEach((item) => { if (item.share_ref) ensureSharePreview(item.share_ref); });
            const latestSeq = page.items?.length ? page.items[page.items.length - 1].seq : null;
            if (latestSeq) messagesApi.markRead(conversationId, latestSeq).catch(() => {});
            requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: reset ? "auto" : "smooth" }));
        } catch (error) {
            const status = error?.response?.status;
            setThreadNotice(status === 403 ? "This conversation is no longer available to your account." : status === 404 ? "That conversation is gone or unavailable." : "Could not load this conversation.");
            toast.error(threadNotice || "Could not load this conversation.");
        } finally {
            setLoadingMessages(false);
        }
    }, [ensureSharePreview, threadNotice]);

    const syncConversation = useCallback((updatedConversation) => {
        if (!updatedConversation?.id) return;
        setActiveConversation((prev) => (prev?.id === updatedConversation.id ? { ...prev, ...updatedConversation } : prev));
        setConversations((prev) => prev.map((item) => (item.id === updatedConversation.id ? { ...item, ...updatedConversation } : item)));
    }, []);

    const handleGroupConversationExit = useCallback(() => {
        if (activeConversation?.id) delete conversationDraftsRef.current[activeConversation.id];
        setActiveConversation(null);
        setShowGroupDrawer(false);
        loadShell();
    }, [activeConversation?.id, loadShell]);

    useEffect(() => {
        loadShell();
    }, [loadShell]);

    useEffect(() => {
        if (!activeConversation?.id) return;
        messages.forEach((item) => { if (item.share_ref) ensureSharePreview(item.share_ref); });
    }, [messages, activeConversation?.id, ensureSharePreview]);

    useEffect(() => {
        recordingSecondsRef.current = recordingSeconds;
    }, [recordingSeconds]);

    useEffect(() => {
        recordingWaveformRef.current = recordingWaveform;
    }, [recordingWaveform]);

    useEffect(() => { textRef.current = text; }, [text]);
    useEffect(() => { replyToRef.current = replyTo; }, [replyTo]);
    useEffect(() => { shareDraftRef.current = shareDraft; }, [shareDraft]);

    useEffect(() => {
        const prevId = activeConversationIdRef.current;
        const nextId = activeConversation?.id;

        // 1. Save outgoing conversation draft before switching
        if (prevId && prevId !== nextId) {
            const currentText = textRef.current || "";
            const currentReply = replyToRef.current;
            const currentShare = shareDraftRef.current;
            if (currentText.trim() || currentReply || currentShare) {
                conversationDraftsRef.current[prevId] = {
                    text: currentText,
                    replyTo: currentReply,
                    shareDraft: currentShare,
                };
            } else {
                delete conversationDraftsRef.current[prevId];
            }
        }
        activeConversationIdRef.current = nextId;

        // 2. Restore incoming conversation draft
        if (nextId) {
            const saved = conversationDraftsRef.current[nextId] || {};
            const restoredText = saved.text || "";
            setText(restoredText);
            textRef.current = restoredText;
            setReplyTo(saved.replyTo || null);
            replyToRef.current = saved.replyTo || null;
            setShareDraft(saved.shareDraft || null);
            shareDraftRef.current = saved.shareDraft || null;
            requestAnimationFrame(() => {
                if (textareaRef.current) {
                    textareaRef.current.style.height = "auto";
                    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 110)}px`;
                }
            });
        } else {
            setText("");
            textRef.current = "";
            setReplyTo(null);
            replyToRef.current = null;
            setShareDraft(null);
            shareDraftRef.current = null;
        }
        clearAttachmentDrafts();
    }, [activeConversation?.id, clearAttachmentDrafts]);

    useEffect(() => {
        if (!initialComposeHandle) {
            composeHandledRef.current = false;
            return;
        }
        if (composeHandledRef.current || loadingShell) return;
        setShowNewModal(true);
        composeHandledRef.current = true;
    }, [initialComposeHandle, loadingShell]);

    useEffect(() => {
        if (!initialConvId || loadingShell) return;
        loadConversation(initialConvId);
    }, [initialConvId, loadingShell, loadConversation]);

    const onRealtimeEvent = useCallback((event) => {
        if (event.type === "conversation.message.created") {
            if (event.message?.share_ref) ensureSharePreview(event.message.share_ref);
            setConversations((prev) => event.message ? upsertConversation(prev, {
                ...(prev.find((item) => item.id === event.conversation_id) || activeConversation || {}),
                id: event.conversation_id,
                last_message_id: event.message.id,
                last_message_at: event.message.created_at,
                last_message_preview: event.message.text || (event.message.type === "voice" ? "Voice message" : event.message.type === "share" ? "Shared Passport content" : "Attachment"),
                last_message_type: event.message.type,
                last_sender_id: event.message.sender_id,
                unread_count: event.message.sender_id === user?.id ? 0 : ((prev.find((item) => item.id === event.conversation_id)?.unread_count || 0) + 1),
            }) : prev);
            if (event.conversation_id === activeConversation?.id && event.message) {
                setMessages((prev) => dedupeMessages([...prev.filter((item) => !String(item.id).startsWith("temp-")), event.message]));
                if (event.message.sender_id !== user?.id) {
                    messagesApi.markRead(activeConversation.id, event.message.seq).catch(() => {});
                }
                requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }));
            }
        }
        if (event.type === "conversation.message.updated" || event.type === "conversation.message.deleted") {
            if (event.message?.share_ref) ensureSharePreview(event.message.share_ref);
            setMessages((prev) => prev.map((item) => item.id === event.message?.id ? event.message : item));
        }
        if (event.type === "conversation.read.updated") {
            if (event.conversation_id === activeConversation?.id && event.user_id !== user?.id) {
                setMessages((prev) => prev.map((item) => item.sender_id === user?.id && (item.seq || 0) <= (event.last_read_seq || 0) ? { ...item, read: true } : item));
            }
        }
        if (event.type === "inbox.updated" && event.conversation) {
            setConversations((prev) => upsertConversation(prev, event.conversation));
        }
        if (event.type === "typing.start" && event.user_id !== user?.id) {
            setTypingUsers((prev) => ({
                ...prev,
                [event.conversation_id]: {
                    ...(prev[event.conversation_id] || {}),
                    [event.user_id]: event.user_name || event.user_handle || "Someone",
                },
            }));
        }
        if (event.type === "typing.stop" && event.user_id !== user?.id) {
            setTypingUsers((prev) => {
                const next = { ...prev };
                if (next[event.conversation_id]) {
                    const convo = { ...next[event.conversation_id] };
                    delete convo[event.user_id];
                    if (Object.keys(convo).length === 0) delete next[event.conversation_id];
                    else next[event.conversation_id] = convo;
                }
                return next;
            });
        }
        if (event.type === "conversation.member.updated") {
            if (event.member?.user_id === user?.id && event.member?.state === "removed") {
                toast.error("You were removed from this group.");
                if (activeConversation?.id === event.conversation_id) {
                    setActiveConversation(null);
                    setShowGroupDrawer(false);
                }
                setConversations((prev) => prev.filter((item) => item.id !== event.conversation_id));
            } else if (event.conversation_id === activeConversation?.id) {
                setActiveConversation((prev) => {
                    if (!prev || prev.id !== event.conversation_id) return prev;
                    let newMembers = prev.members || [];
                    if (event.member?.state === "removed" || event.member?.state === "left") {
                        newMembers = newMembers.filter((m) => m.user_id !== event.member.user_id);
                    } else if (event.member?.user_id) {
                        const exists = newMembers.some((m) => m.user_id === event.member.user_id);
                        newMembers = exists ? newMembers.map((m) => (m.user_id === event.member.user_id ? { ...m, ...event.member } : m)) : [...newMembers, event.member];
                    }
                    return { ...prev, members: newMembers, member_count: newMembers.length };
                });
            }
            if (event.member?.state === "active") {
                messagesApi.conversation(event.conversation_id).then((conv) => {
                    syncConversation(conv);
                }).catch(() => {});
            }
        }
        if (event.type === "conversation.ownership.transferred") {
            if (event.conversation_id === activeConversation?.id) {
                if (event.new_creator_id === user?.id) {
                    toast.success("You are now the group creator.");
                }
                messagesApi.conversation(event.conversation_id).then((conv) => {
                    syncConversation(conv);
                }).catch(() => {});
            }
        }
        if (event.type === "conversation.deleted") {
            delete conversationDraftsRef.current[event.conversation_id];
            if (activeConversation?.id === event.conversation_id) {
                toast.error("This group was deleted by the creator.");
                setActiveConversation(null);
                setShowGroupDrawer(false);
            }
            if (activeRequest?.type === "invite" && (activeRequest?.data?.conversation_id === event.conversation_id || activeRequest?.data?.conversation?.id === event.conversation_id)) {
                toast.error("This group was deleted by the creator.");
                setActiveRequest(null);
            }
            setConversations((prev) => prev.filter((item) => item.id !== event.conversation_id));
            setGroupInvites((prev) => prev.filter((item) => item.conversation_id !== event.conversation_id && item.conversation?.id !== event.conversation_id));
        }
        if (event.type === "message.request.created" && event.request) {
            setRequests((prev) => {
                const currentIncoming = prev.incoming || [];
                const exists = currentIncoming.some((r) => r.id === event.request.id);
                const incoming = exists ? currentIncoming.map((r) => (r.id === event.request.id ? event.request : r)) : [event.request, ...currentIncoming];
                return {
                    ...prev,
                    incoming,
                    incoming_count: incoming.length,
                };
            });
            if (event.request.share_ref) ensureSharePreview(event.request.share_ref);
            toast.info(`New message request from @${event.request.sender_handle || "a traveler"}`);
        }
        if (event.type === "group.invite.created" && event.invite) {
            setGroupInvites((prev) => {
                const exists = prev.some((inv) => inv.id === event.invite.id);
                return exists ? prev.map((inv) => (inv.id === event.invite.id ? event.invite : inv)) : [event.invite, ...prev];
            });
            toast.info(`You were invited to "${event.invite.conversation?.title || "a group"}"`);
        }
        if (event.type === "message.request.declined" && event.request_id) {
            setRequests((prev) => {
                const incoming = (prev.incoming || []).filter((r) => r.id !== event.request_id);
                return {
                    ...prev,
                    incoming,
                    incoming_count: incoming.length,
                };
            });
            if (activeRequest?.type === "message" && activeRequest?.data?.id === event.request_id) {
                setActiveRequest(null);
            }
        }
        if (event.type === "message.request.accepted") {
            if (event.request_id) {
                setRequests((prev) => {
                    const incoming = (prev.incoming || []).filter((r) => r.id !== event.request_id);
                    return {
                        ...prev,
                        incoming,
                        incoming_count: incoming.length,
                    };
                });
                if (activeRequest?.type === "message" && activeRequest?.data?.id === event.request_id) {
                    setActiveRequest(null);
                }
            }
            if (event.conversation) {
                setConversations((prev) => upsertConversation(prev, event.conversation));
                toast.success("Your message request was accepted!");
            }
        }
    }, [activeConversation, activeRequest, ensureSharePreview, syncConversation, user?.id]);

    const realtime = useMessagingRealtime(Boolean(user), onRealtimeEvent);

    const filteredConversations = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return conversations;
        return conversations.filter((item) => [item.title, item.other_user_name, item.other_user_handle, item.last_message_preview].join(" ").toLowerCase().includes(q));
    }, [conversations, search]);

    const filteredGroupConversations = useMemo(() => {
        const q = search.trim().toLowerCase();
        const groups = conversations.filter((item) => item.type === "group");
        if (!q) return groups;
        return groups.filter((item) => [item.title, item.last_message_preview].join(" ").toLowerCase().includes(q));
    }, [conversations, search]);

    const groupCount = useMemo(() => conversations.filter((item) => item.type === "group").length, [conversations]);
    const groupUnread = useMemo(() => conversations.filter((item) => item.type === "group").reduce((sum, item) => sum + (item.unread_count || 0), 0), [conversations]);

    const filteredIncomingRequests = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return requests.incoming || [];
        return (requests.incoming || []).filter((item) => [item.sender_name, item.sender_handle, item.text].join(" ").toLowerCase().includes(q));
    }, [requests.incoming, search]);

    const filteredGroupInvites = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return groupInvites;
        return groupInvites.filter((item) => [item.conversation?.title, item.inviter_handle, item.invitee_name].join(" ").toLowerCase().includes(q));
    }, [groupInvites, search]);

    const totalUnread = useMemo(() => conversations.reduce((sum, item) => sum + (item.unread_count || 0), 0), [conversations]);
    const requestCount = (requests.incoming_count || 0) + (groupInvites.length || 0);
    const activeTyping = useMemo(() => {
        const typers = activeConversation?.id ? typingUsers[activeConversation.id] : null;
        if (!typers || typeof typers !== "object") return null;
        const names = Object.values(typers);
        if (names.length === 0) return null;
        if (names.length === 1) return `${names[0]} is typing…`;
        if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
        return `${names[0]}, ${names[1]} and ${names.length - 2} more are typing…`;
    }, [typingUsers, activeConversation?.id]);
    const composerHasFailedDraft = attachmentDrafts.some((item) => item.status === "error");
    const composerHasContent = Boolean(text.trim() || shareDraft || attachmentDrafts.length);

    const handleOpenConversation = async (conversation) => {
        setThreadNotice("");
        await loadConversation(conversation.id);
    };

    const loadMoreMessages = async () => {
        if (!activeConversation?.id || !nextCursor) return;
        try {
            const page = await messagesApi.messages(activeConversation.id, { limit: 30, before_seq: nextCursor });
            setMessages((prev) => dedupeMessages([...(page.items || []), ...prev]));
            setNextCursor(page.next_cursor || null);
        } catch {
            toast.error("Could not load older messages.");
        }
    };

    const stopTyping = () => {
        if (activeConversation?.id) realtime.send({ type: "typing.stop", conversation_id: activeConversation.id });
    };

    const emitTyping = () => {
        if (!activeConversation?.id) return;
        realtime.send({ type: "typing.start", conversation_id: activeConversation.id });
        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(stopTyping, 1200);
    };

    const handleTextChange = (value) => {
        setText(value);
        textRef.current = value;
        if (activeConversation?.id) {
            conversationDraftsRef.current[activeConversation.id] = {
                ...(conversationDraftsRef.current[activeConversation.id] || {}),
                text: value,
            };
        }
        if (value.trim()) emitTyping();
        else stopTyping();
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 110)}px`;
        }
    };

    const uploadDraftById = useCallback(async (draftId) => {
        const current = attachmentDrafts.find((item) => item.id === draftId);
        if (!current?.file || !activeConversation?.id) return null;
        if (current.serverAttachment && current.status === "ready") return current.serverAttachment;
        const controller = new AbortController();
        uploadControllersRef.current[draftId] = controller;
        setAttachmentDrafts((prev) => prev.map((item) => item.id === draftId ? { ...item, status: "uploading", progress: item.progress || 2, error: null } : item));
        try {
            const attachment = await messagesApi.uploadAttachment(
                activeConversation.id,
                current.file,
                {
                    kind_hint: current.kind === "voice" ? "voice" : undefined,
                    duration_ms: current.durationMs || undefined,
                    waveform: current.kind === "voice" ? (current.waveform || undefined) : undefined,
                    signal: controller.signal,
                },
                (event) => {
                    if (!event.total) return;
                    setAttachmentDrafts((prev) => prev.map((item) => item.id === draftId ? { ...item, progress: Math.max(2, Math.round((event.loaded / event.total) * 100)) } : item));
                },
            );
            setAttachmentDrafts((prev) => prev.map((item) => item.id === draftId ? { ...item, status: "ready", progress: 100, serverAttachment: attachment, error: null } : item));
            return attachment;
        } catch (error) {
            if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
                setAttachmentDrafts((prev) => prev.map((item) => item.id === draftId ? { ...item, status: "queued", progress: 0, error: null } : item));
                return null;
            }
            setAttachmentDrafts((prev) => prev.map((item) => item.id === draftId ? { ...item, status: "error", error: error?.response?.data?.detail || "Upload failed", progress: 0 } : item));
            throw error;
        } finally {
            delete uploadControllersRef.current[draftId];
        }
    }, [activeConversation?.id, attachmentDrafts]);

    const cancelDraftUpload = useCallback((draftId) => {
        uploadControllersRef.current[draftId]?.abort?.();
    }, []);

    const removeDraft = useCallback((draftId) => {
        uploadControllersRef.current[draftId]?.abort?.();
        setAttachmentDrafts((prev) => {
            const match = prev.find((item) => item.id === draftId);
            if (match) revokeDraftPreview(match);
            return prev.filter((item) => item.id !== draftId);
        });
    }, [revokeDraftPreview]);

    const retryDraftUpload = useCallback(async (draftId) => {
        setAttachmentDrafts((prev) => prev.map((item) => item.id === draftId ? { ...item, status: "queued", progress: 0, error: null } : item));
        try {
            await uploadDraftById(draftId);
        } catch (_) {}
    }, [uploadDraftById]);

    const prepareAttachmentIdsForSend = useCallback(async () => {
        const drafts = attachmentDrafts;
        if (!drafts.length) return [];
        if (drafts.some((item) => item.status === "error")) {
            toast.error("Retry or remove failed attachments before sending.");
            return null;
        }
        const attachmentIds = [];
        for (const draft of drafts) {
            const ready = draft.serverAttachment || (draft.status === "ready" ? draft.serverAttachment : null);
            if (ready?.media_id) {
                attachmentIds.push(ready.media_id);
                continue;
            }
            const uploaded = await uploadDraftById(draft.id);
            if (!uploaded?.media_id) return null;
            attachmentIds.push(uploaded.media_id);
        }
        return attachmentIds;
    }, [attachmentDrafts, uploadDraftById]);

    const sendMessage = async (payload = {}, options = {}) => {
        if (!activeConversation?.id || sending) return;
        const finalText = payload.text != null ? payload.text : text;
        const finalShare = payload.share_ref != null ? payload.share_ref : (shareDraft?.shareRef || null);
        const finalReplyTo = payload.reply_to_message_id != null ? payload.reply_to_message_id : replyTo?.id || null;
        let attachmentIds = payload.attachment_ids || [];
        const optimisticId = `temp-${Date.now()}`;

        setSending(true);
        try {
            if (!attachmentIds.length && attachmentDrafts.length) {
                const preparedIds = await prepareAttachmentIdsForSend();
                if (preparedIds === null) {
                    setSending(false);
                    return;
                }
                attachmentIds = preparedIds;
            }
            if (!String(finalText || "").trim() && !attachmentIds.length && !finalShare) {
                setSending(false);
                return;
            }
            const skipOptimistic = options.skipOptimistic || attachmentIds.length > 0;
            if (!skipOptimistic) {
                setMessages((prev) => [...prev, {
                    id: optimisticId,
                    seq: (prev[prev.length - 1]?.seq || 0) + 0.01,
                    conversation_id: activeConversation.id,
                    sender_id: user.id,
                    sender_name: user.name,
                    sender_handle: user.handle,
                    sender_avatar: user.avatar_url,
                    text: String(finalText || "").trim() || null,
                    type: finalShare ? "share" : "text",
                    attachments: [],
                    share_ref: finalShare,
                    reply_to_message_id: finalReplyTo,
                    reply_preview: replyTo || null,
                    status: "sending",
                    reaction_counts: {},
                    reactions: [],
                    created_at: new Date().toISOString(),
                }]);
            }
            const sent = await messagesApi.sendMessage(activeConversation.id, {
                text: String(finalText || "").trim() || undefined,
                attachment_ids: attachmentIds,
                reply_to_message_id: finalReplyTo || undefined,
                share_ref: finalShare || undefined,
            });
            if (sent.share_ref) ensureSharePreview(sent.share_ref);
            setMessages((prev) => dedupeMessages(prev.filter((item) => item.id !== optimisticId).concat(sent)));
            setConversations((prev) => upsertConversation(prev, {
                ...(prev.find((item) => item.id === activeConversation.id) || activeConversation),
                last_message_id: sent.id,
                last_message_at: sent.created_at,
                last_message_preview: sent.text || (sent.type === "voice" ? "Voice message" : sent.type === "share" ? "Shared Passport content" : "Attachment"),
                last_message_type: sent.type,
                unread_count: 0,
            }));
            if (activeConversation?.id) {
                delete conversationDraftsRef.current[activeConversation.id];
            }
            setText("");
            textRef.current = "";
            if (textareaRef.current) textareaRef.current.style.height = "auto";
            setReplyTo(null);
            replyToRef.current = null;
            setShareDraft(null);
            shareDraftRef.current = null;
            clearAttachmentDrafts();
            stopTyping();
            requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }));
        } catch (error) {
            setMessages((prev) => prev.filter((item) => item.id !== optimisticId));
            const status = error?.response?.status;
            if (status === 403) setThreadNotice("Passport blocked this message based on the latest privacy or block settings.");
            toast.error(error?.response?.data?.detail || "Message could not be sent.");
        } finally {
            setSending(false);
        }
    };

    const startVoiceMessage = async () => {
        if (!activeConversation?.id) return toast.error("Open a conversation first.");
        if (!("MediaRecorder" in window) || !navigator.mediaDevices?.getUserMedia) {
            toast.error("Voice messages are unavailable on this browser.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            recordingStreamRef.current = stream;
            mediaChunksRef.current = [];
            discardRecordingRef.current = false;
            setRecordingWaveform([]);
            const recorder = new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 64;
            const source = audioContext.createMediaStreamSource(stream);
            source.connect(analyser);
            audioContextRef.current = audioContext;
            analyserRef.current = analyser;

            const sampleWaveform = () => {
                const values = new Uint8Array(analyser.frequencyBinCount);
                analyser.getByteFrequencyData(values);
                const average = values.reduce((sum, value) => sum + value, 0) / Math.max(values.length, 1);
                setRecordingWaveform((prev) => [...prev.slice(-23), Math.max(4, Math.round((average / 255) * 24))]);
                recordingFrameRef.current = requestAnimationFrame(sampleWaveform);
            };
            sampleWaveform();
            recorder.ondataavailable = (event) => { if (event.data?.size) mediaChunksRef.current.push(event.data); };
            recorder.onstop = async () => {
                clearInterval(intervalRef.current);
                if (recordingFrameRef.current) cancelAnimationFrame(recordingFrameRef.current);
                await audioContextRef.current?.close?.().catch?.(() => {});
                audioContextRef.current = null;
                analyserRef.current = null;
                setRecording(false);
                stream.getTracks().forEach((track) => track.stop());
                const durationMs = recordingSecondsRef.current * 1000;
                const waveform = [...recordingWaveformRef.current];
                setRecordingSeconds(0);
                if (discardRecordingRef.current || !mediaChunksRef.current.length) {
                    setRecordingWaveform([]);
                    return;
                }
                const rawMime = recorder.mimeType || "audio/webm";
                const cleanMime = rawMime.split(";")[0].trim() || "audio/webm";
                const ext = cleanMime.includes("ogg") ? "ogg" : cleanMime.includes("mp4") ? "m4a" : "webm";
                const blob = new Blob(mediaChunksRef.current, { type: cleanMime });
                const file = new File([blob], `passport-voice-${Date.now()}.${ext}`, { type: cleanMime });
                setRecordingWaveform([]);
                setAttachmentDrafts((prev) => [...prev, {
                    id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    file,
                    name: file.name,
                    mime: cleanMime,
                    kind: "voice",
                    previewUrl: null,
                    durationMs,
                    waveform: waveform.length ? waveform : [6, 10, 13, 9, 15, 11, 7, 12],
                    status: "queued",
                    progress: 0,
                    serverAttachment: null,
                    error: null,
                }].slice(0, 10));
            };
            recorder.start();
            setRecording(true);
            intervalRef.current = setInterval(() => setRecordingSeconds((prev) => prev + 1), 1000);
        } catch (_) {
            toast.error("Passport couldn’t access your microphone.");
        }
    };

    const stopVoiceMessage = () => {
        try { mediaRecorderRef.current?.stop(); } catch (_) {}
    };

    const cancelVoiceMessage = () => {
        discardRecordingRef.current = true;
        try { mediaRecorderRef.current?.stop(); } catch (_) {}
        // Immediately release mic hardware so the browser tab recording indicator clears at once.
        try { recordingStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    };

    // ── Search jump: close drawer, scroll to message, flash gold highlight ──
    const handleMessageJump = useCallback((messageId) => {
        setShowGroupDrawer(false);
        requestAnimationFrame(() => {
            const el = document.getElementById(`chat-message-row-${messageId}`);
            if (!el) return;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.style.transition = "background 0.2s ease";
            el.style.background = "rgba(212,175,55,0.18)";
            setTimeout(() => { el.style.background = ""; }, 1600);
        });
    }, []);

    const toggleSaveMessage = async (message) => {
        const nextSaved = !savedMessageIds.has(message.id);
        setSavedMessageIds((prev) => {
            const next = new Set(prev);
            if (nextSaved) next.add(message.id); else next.delete(message.id);
            return next;
        });
        try {
            if (nextSaved) await messagesApi.saveMessage(message.id);
            else await messagesApi.unsaveMessage(message.id);
            const saved = await messagesApi.savedMessages({ limit: 30 });
            setSavedItems(saved.items || []);
            setSavedMessageIds(new Set((saved.items || []).map((item) => item.message?.id).filter(Boolean)));
        } catch {
            toast.error("Could not update your saved messages.");
        }
    };

    const reactToMessage = async (message, emoji) => {
        try {
            const updated = await messagesApi.reactMessage(message.id, emoji);
            if (updated.share_ref) ensureSharePreview(updated.share_ref);
            setMessages((prev) => prev.map((item) => item.id === updated.id ? updated : item));
        } catch {
            toast.error("Could not update that reaction.");
        }
    };

    const unsendMessage = async (message) => {
        try {
            const updated = await messagesApi.unsendMessage(message.id);
            setMessages((prev) => prev.map((item) => item.id === updated.id ? updated : item));
        } catch (error) {
            toast.error(error?.response?.data?.detail || "That message can’t be unsent anymore.");
        }
    };

    const deleteForMe = async (message) => {
        try {
            await messagesApi.deleteForMe(message.id);
            setMessages((prev) => prev.filter((item) => item.id !== message.id));
            setSavedMessageIds((prev) => { const next = new Set(prev); next.delete(message.id); return next; });
        } catch {
            toast.error("Could not remove that message from your view.");
        }
    };

    const acceptRequest = async (request) => {
        try {
            const result = await messagesApi.acceptRequest(request.id);
            setActiveRequest(null);
            await loadShell();
            setTab("inbox");
            if (result?.conversation?.id) {
                await handleOpenConversation(result.conversation);
            }
            toast.success("Message request accepted.");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not accept that request.");
        }
    };

    const declineRequest = async (requestId) => {
        try {
            await messagesApi.declineRequest(requestId);
            setActiveRequest(null);
            await loadShell();
            toast.success("Message request cleared.");
        } catch {
            toast.error("Could not clear that request.");
        }
    };

    const blockRequestSender = async (request) => {
        try {
            await messagesApi.blockUser(request.sender_handle);
            await messagesApi.declineRequest(request.id);
            setActiveRequest(null);
            await loadShell();
            toast.success("User blocked from messaging you.");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not block that user.");
        }
    };

    const acceptGroupInvite = async (invite) => {
        try {
            const res = await messagesApi.acceptInvite(invite.id);
            setActiveRequest(null);
            await loadShell();
            setTab("inbox");
            const conversationId = res?.conversation_id || invite.conversation_id || invite.conversation?.id;
            if (conversationId) {
                await handleOpenConversation({ id: conversationId });
            }
            toast.success("Group invite accepted.");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Could not accept that invite.");
        }
    };

    const declineGroupInvite = async (invite) => {
        try {
            await messagesApi.declineInvite(invite.id);
            setActiveRequest(null);
            await loadShell();
            toast.success("Invite removed.");
        } catch {
            toast.error("Could not remove that invite.");
        }
    };

    if (loadingShell && !conversations.length && !(requests.incoming || []).length) {
        return <div className="min-h-[60vh] grid place-items-center text-white/60" data-testid="messages-loading"><Spinner size={28} className="animate-spin" /></div>;
    }

    return (
        <div className={standalone ? "min-h-screen grain" : ""} data-testid="messages-screen">
            <div className={standalone ? "max-w-[1440px] mx-auto px-4 md:px-8 py-3 md:py-5" : ""}>
                <div className="rounded-[32px] overflow-hidden relative h-[calc(100vh-2.5rem)] max-h-[920px] flex flex-col" style={{ background: "linear-gradient(180deg, rgba(11,19,32,0.96), rgba(5,10,17,0.98))", border: "1px solid rgba(212,175,55,0.18)", boxShadow: "0 30px 80px rgba(0,0,0,0.35)" }} data-testid="messages-panel">
                    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at top right, rgba(212,175,55,0.12), transparent 30%), radial-gradient(circle at bottom left, rgba(194,207,197,0.08), transparent 28%)" }} />

                    <div className="relative z-10 grid lg:grid-cols-[340px_1fr] flex-1 min-h-0 h-full overflow-hidden">
                        <aside className={`${activeConversation || activeRequest ? "hidden lg:flex" : "flex"} flex-col h-full border-r overflow-hidden`} style={{ borderColor: "rgba(255,255,255,0.06)" }} data-testid="messages-sidebar">
                            <div className="px-4 md:px-5 py-4 border-b shrink-0" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="overline mb-1 text-[10px]">Passport messenger</div>
                                        <h1 className="font-serif text-2xl text-white tracking-tight" data-testid="messages-heading">Your messages</h1>
                                    </div>
                                    {standalone && <Link to="/app" className="w-9 h-9 rounded-full glass grid place-items-center" data-testid="messages-back-to-app"><ArrowLeft size={15} /></Link>}
                                </div>

                                <div className="mt-3 flex items-center gap-2" data-testid="messages-connection-chip">
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] border" style={{ background: realtime.status === "connected" ? "rgba(34,197,94,0.1)" : "rgba(255,255,255,0.04)", borderColor: realtime.status === "connected" ? "rgba(34,197,94,0.25)" : "rgba(255,255,255,0.08)", color: realtime.status === "connected" ? "#86efac" : "rgba(255,255,255,0.65)" }}>
                                        {realtime.status === "connected" ? <WifiHigh size={13} /> : <WifiSlash size={13} />}
                                        {realtime.status === "connected" ? "Live" : realtime.status === "offline" ? "Offline" : realtime.status === "reconnecting" ? "Reconnecting" : "Connecting"}
                                    </span>
                                </div>

                                <div className="mt-3 flex gap-1.5" data-testid="messages-tab-bar">
                                    <button onClick={() => { setTab("inbox"); setSearch(""); }} className="flex-1 px-3 py-2 rounded-full text-xs font-semibold transition-all" style={{ background: tab === "inbox" ? "var(--gold)" : "rgba(255,255,255,0.04)", color: tab === "inbox" ? "var(--navy)" : "white" }} data-testid="inbox-tab-button">
                                        Inbox {totalUnread > 0 && <span data-testid="messages-unread-badge">· {totalUnread}</span>}
                                    </button>
                                    <button onClick={() => { setTab("groups"); setSearch(""); }} className="flex-1 px-3 py-2 rounded-full text-xs font-semibold transition-all" style={{ background: tab === "groups" ? "var(--gold)" : "rgba(255,255,255,0.04)", color: tab === "groups" ? "var(--navy)" : "white" }} data-testid="groups-tab-button">
                                        Groups {groupUnread > 0 && <span data-testid="groups-unread-badge">· {groupUnread}</span>}
                                    </button>
                                    <button onClick={() => { setTab("requests"); setSearch(""); }} className="flex-1 px-3 py-2 rounded-full text-xs font-semibold transition-all" style={{ background: tab === "requests" ? "var(--gold)" : "rgba(255,255,255,0.04)", color: tab === "requests" ? "var(--navy)" : "white" }} data-testid="requests-tab-button">
                                        Requests {requestCount > 0 && <span>· {requestCount}</span>}
                                    </button>
                                </div>

                                <div className="relative mt-3">
                                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tab === "inbox" ? "Search conversations" : "Search requests"} className="w-full rounded-2xl px-3.5 py-2 bg-black/30 text-white border text-xs outline-none" style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="inbox-search-input" />
                                </div>

                                <div className="mt-3 flex gap-2">
                                    <button onClick={() => setShowNewModal(true)} className="btn-gold text-xs flex-1 !py-2 !px-3.5 justify-center font-medium" data-testid="messages-new-conversation-button"><Plus size={13} weight="bold" /> New chat</button>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-2.5 md:p-3 space-y-1.5" data-testid="messages-sidebar-list">
                                {(tab === "inbox" || tab === "groups") && (() => {
                                    const list = tab === "groups" ? filteredGroupConversations : filteredConversations;
                                    return list.map((conversation) => {
                                        const active = activeConversation?.id === conversation.id;
                                        const isGroup = conversation.type === "group";
                                        return (
                                            <button key={conversation.id} onClick={() => handleOpenConversation(conversation)} className="w-full text-left rounded-2xl px-3 py-2.5 transition-colors" style={{ background: active ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.02)", border: `1px solid ${active ? "rgba(212,175,55,0.24)" : "rgba(255,255,255,0.04)"}` }} data-testid={`conversation-item-${conversation.id}`}>
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-10 h-10 rounded-full overflow-hidden shrink-0" style={{ background: "rgba(255,255,255,0.06)" }}>
                                                        {isGroup ? (
                                                            <ProtectedConversationAvatar
                                                                mediaId={conversation.avatar_media_id}
                                                                className="w-full h-full object-cover"
                                                                testId={`conversation-avatar-${conversation.id}`}
                                                                fallback={<div className="w-full h-full grid place-items-center text-white/70"><UsersThree size={16} /></div>}
                                                            />
                                                        ) : <img src={conversation.other_user_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${conversation.other_user_handle || conversation.id}`} alt="" className="w-full h-full object-cover" />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between gap-1.5">
                                                            <div className="text-xs md:text-sm font-medium text-white truncate">{conversation.title || conversation.other_user_name || "Passport chat"}</div>
                                                            <div className="text-[10px] text-white/40 shrink-0">{timeAgo(conversation.last_message_at)}</div>
                                                        </div>
                                                        <div className="text-[11px] text-white/50 truncate mt-0.5">{conversation.last_message_preview || (isGroup ? "No messages yet" : `@${conversation.other_user_handle}`)}</div>
                                                    </div>
                                                    {conversation.unread_count > 0 && <span className="min-w-[20px] h-[20px] px-1 rounded-full grid place-items-center text-[10px] font-bold shrink-0" style={{ background: "var(--gold)", color: "var(--navy)" }}>{conversation.unread_count}</span>}
                                                </div>
                                            </button>
                                        );
                                    });
                                })()}
                                {tab === "groups" && filteredGroupConversations.length === 0 && (
                                    <div className="rounded-[26px] px-5 py-12 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.08)" }} data-testid="messages-groups-empty">
                                        <div className="font-serif text-2xl text-white mb-2">No groups yet.</div>
                                        <div className="text-sm text-white/45">Create a group chat to travel together with multiple Passport friends at once.</div>
                                    </div>
                                )}
                                {tab === "requests" && (
                                    <>
                                        <div className="px-2 pt-1 pb-1 text-[10px] uppercase tracking-[0.25em] text-white/40">Message requests</div>
                                        {filteredIncomingRequests.map((request) => (
                                            <button key={request.id} onClick={() => { setActiveRequest({ type: "message", data: request }); setActiveConversation(null); if (request.share_ref) ensureSharePreview(request.share_ref); }} className="w-full text-left rounded-2xl px-3 py-2.5 transition-colors" style={{ background: activeRequest?.data?.id === request.id ? "rgba(244,162,143,0.12)" : "rgba(255,255,255,0.02)", border: `1px solid ${activeRequest?.data?.id === request.id ? "rgba(244,162,143,0.2)" : "rgba(255,255,255,0.04)"}` }} data-testid={`request-item-${request.id}`}>
                                                <div className="flex items-center gap-2.5">
                                                    <img src={request.sender_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${request.sender_handle}`} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between gap-1.5">
                                                            <div className="text-xs md:text-sm font-medium text-white truncate">{request.sender_name}</div>
                                                            <div className="text-[10px] text-white/40 shrink-0">{timeAgo(request.created_at)}</div>
                                                        </div>
                                                        <div className="text-[11px] text-white/50 truncate mt-0.5">{request.text || "Shared Passport content"}</div>
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                        <div className="px-2 pt-3 pb-1 text-[10px] uppercase tracking-[0.25em] text-white/40">Group invites</div>
                                        {filteredGroupInvites.map((invite) => (
                                            <button key={invite.id} onClick={() => { setActiveRequest({ type: "invite", data: invite }); setActiveConversation(null); }} className="w-full text-left rounded-2xl px-3 py-2.5 transition-colors" style={{ background: activeRequest?.data?.id === invite.id ? "rgba(159,213,208,0.12)" : "rgba(255,255,255,0.02)", border: `1px solid ${activeRequest?.data?.id === invite.id ? "rgba(159,213,208,0.2)" : "rgba(255,255,255,0.04)"}` }} data-testid={`group-invite-item-${invite.id}`}>
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(255,255,255,0.06)" }}><UsersThree size={16} /></div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between gap-1.5">
                                                            <div className="text-xs md:text-sm font-medium text-white truncate">{invite.conversation?.title || "Group chat"}</div>
                                                            <div className="text-[10px] text-white/40 shrink-0">{timeAgo(invite.created_at)}</div>
                                                        </div>
                                                        <div className="text-[11px] text-white/50 truncate mt-0.5">Invited by @{invite.inviter_handle || invite.inviter_name || invite.inviter_id}</div>
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                        {!filteredIncomingRequests.length && !filteredGroupInvites.length && (
                                            <div className="rounded-[26px] px-5 py-12 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.08)" }} data-testid="messages-requests-empty">
                                                <div className="font-serif text-2xl text-white mb-2">No requests waiting.</div>
                                                <div className="text-sm text-white/45">Unknown travelers and private outreach will appear here instead of inside your main inbox.</div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {tab === "inbox" && filteredConversations.length === 0 && (
                                    <div className="rounded-[26px] px-5 py-12 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.08)" }} data-testid="messages-empty-state">
                                        <div className="font-serif text-2xl text-white mb-2">No conversations yet.</div>
                                        <div className="text-sm text-white/45">Start a Passport chat with a traveler you follow, or send a request when privacy asks for it.</div>
                                    </div>
                                )}
                            </div>
                        </aside>

                        <main className={`${activeConversation || activeRequest ? "flex" : "hidden lg:flex"} flex-col h-full overflow-hidden`} data-testid="messages-thread-pane">
                            {!activeConversation && !activeRequest ? (
                                <div className="flex-1 grid place-items-center px-8 text-center" data-testid="messages-empty-thread">
                                    <div>
                                        <div className="w-16 h-16 rounded-full mx-auto mb-5 grid place-items-center" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.18)" }}><Globe size={26} color="#D4AF37" /></div>
                                        <div className="font-serif text-3xl text-white mb-3">Choose a conversation</div>
                                        <div className="text-sm text-white/50 max-w-md">Passport keeps your inbox, requests, group invites, saved highlights, and secure media together in one travel-focused messenger.</div>
                                    </div>
                                </div>
                            ) : activeRequest ? (
                                <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                                    <div className="px-5 md:px-6 py-4 border-b shrink-0 flex items-center gap-3" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                                        <button onClick={() => setActiveRequest(null)} className="w-10 h-10 rounded-full glass grid place-items-center lg:hidden" data-testid="messages-back-mobile"><ArrowLeft size={16} /></button>
                                        <div>
                                            <div className="overline mb-1">Message requests</div>
                                            <div className="font-serif text-3xl text-white">{activeRequest.type === "invite" ? activeRequest.data.conversation?.title || "Group invite" : (activeRequest.data.sender_id === user?.id ? `Request to @${activeRequest.data.recipient_handle || activeRequest.data.recipient_name}` : activeRequest.data.sender_name)}</div>
                                        </div>
                                    </div>
                                    <div className="flex-1 min-h-0 overflow-y-auto px-5 md:px-6 py-6 space-y-5">
                                        {activeRequest.type === "message" ? (
                                            activeRequest.data.sender_id === user?.id ? (
                                                <>
                                                    <div className="rounded-[28px] p-5" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.22)" }} data-testid="message-request-banner">
                                                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3"><PaperPlaneRight size={14} weight="fill" /> Pending approval</div>
                                                        <div className="text-sm text-white/70">You sent this message request. It is waiting for @{activeRequest.data.recipient_handle || activeRequest.data.recipient_name || "recipient"} to accept before normal chat access begins.</div>
                                                    </div>
                                                    <div className="rounded-[28px] p-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`message-request-detail-${activeRequest.data.id}`}>
                                                        <div className="flex items-center gap-3 mb-4">
                                                            <img src={user?.avatar_url || `https://api.dicebear.com/7.x/notionists/svg?seed=${user?.handle || user?.id}`} alt="" className="w-14 h-14 rounded-full object-cover" />
                                                            <div>
                                                                <div className="text-white text-lg">To: {activeRequest.data.recipient_name || activeRequest.data.recipient_handle}</div>
                                                                <div className="text-white/45 text-sm">@{activeRequest.data.recipient_handle}</div>
                                                            </div>
                                                        </div>
                                                        {activeRequest.data.text && <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap" data-testid={`message-request-text-${activeRequest.data.id}`}>{activeRequest.data.text}</p>}
                                                        {activeRequest.data.share_ref && (() => {
                                                            const previewKey = `${activeRequest.data.share_ref.entity_type}:${activeRequest.data.share_ref.entity_id}`;
                                                            const preview = sharePreviews[previewKey];
                                                            if (!preview) { ensureSharePreview(activeRequest.data.share_ref); }
                                                            return preview ? (
                                                                <div className="mt-4 rounded-[20px] overflow-hidden" style={{ background: "rgba(11,19,32,0.9)", border: "1px solid rgba(212,175,55,0.2)" }} data-testid={`message-request-share-${activeRequest.data.id}`}>
                                                                    {preview.image_url && (
                                                                        <div className="aspect-[16/9] max-h-28 overflow-hidden bg-black/40">
                                                                            <img src={preview.image_url} alt={preview.title} className="w-full h-full object-cover" />
                                                                        </div>
                                                                    )}
                                                                    <div className="px-3.5 py-3">
                                                                        <div className="text-[9px] uppercase tracking-[0.28em] font-medium mb-0.5" style={{ color: "#D4AF37" }}>Passport {preview.entity_type}</div>
                                                                        <div className="text-xs font-semibold text-white line-clamp-2">{preview.title}</div>
                                                                    </div>
                                                                </div>
                                                            ) : null;
                                                        })()}
                                                    </div>
                                                    <div className="flex gap-3">
                                                        <button onClick={() => declineRequest(activeRequest.data.id)} className="btn-ghost text-sm !py-3 justify-center" data-testid={`request-cancel-button-${activeRequest.data.id}`}><X size={14} /> Cancel Request</button>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    {/* ── Luxury Passport Request Panel ── */}
                                                    {/* Privacy context note */}
                                                    <div
                                                        className="rounded-[24px] px-5 py-4 flex items-start gap-3"
                                                        style={{ background: "rgba(139,157,195,0.07)", border: "1px solid rgba(139,157,195,0.14)" }}
                                                        data-testid="message-request-banner"
                                                    >
                                                        <div className="w-8 h-8 rounded-full grid place-items-center shrink-0 mt-0.5" style={{ background: "rgba(139,157,195,0.12)" }}>
                                                            <ShieldSlash size={15} color="#8B9DC3" />
                                                        </div>
                                                        <div>
                                                            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-1" style={{ color: "#8B9DC3" }}>Message request</div>
                                                            <div className="text-sm text-white/60 leading-relaxed">
                                                                This person wants to connect. They won't know you've seen this until you accept.
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Sender card */}
                                                    <div
                                                        className="rounded-[28px] overflow-hidden"
                                                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
                                                        data-testid={`message-request-detail-${activeRequest.data.id}`}
                                                    >
                                                        <div className="px-5 pt-5 pb-4 flex items-center gap-4">
                                                            <div className="relative shrink-0">
                                                                <img
                                                                    src={activeRequest.data.sender_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${activeRequest.data.sender_handle}`}
                                                                    alt=""
                                                                    className="w-16 h-16 rounded-full object-cover"
                                                                    style={{ border: "2px solid rgba(212,175,55,0.25)" }}
                                                                />
                                                                <div
                                                                    className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full grid place-items-center"
                                                                    style={{ background: "var(--gold)" }}
                                                                >
                                                                    <PaperPlaneRight size={10} color="var(--navy)" weight="fill" />
                                                                </div>
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="font-semibold text-white text-lg leading-tight truncate">{activeRequest.data.sender_name}</div>
                                                                <div className="text-white/45 text-sm">@{activeRequest.data.sender_handle}</div>
                                                            </div>
                                                        </div>

                                                        {/* Message text */}
                                                        {activeRequest.data.text && (
                                                            <div
                                                                className="mx-5 mb-4 rounded-[18px] px-4 py-3.5"
                                                                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
                                                            >
                                                                <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap" data-testid={`message-request-text-${activeRequest.data.id}`}>
                                                                    {activeRequest.data.text}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Shared Passport card preview */}
                                                        {activeRequest.data.share_ref && (() => {
                                                            const previewKey = `${activeRequest.data.share_ref.entity_type}:${activeRequest.data.share_ref.entity_id}`;
                                                            const preview = sharePreviews[previewKey];
                                                            // Trigger fetch if not yet loaded
                                                            if (!preview) { ensureSharePreview(activeRequest.data.share_ref); }
                                                            return preview ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handlePassportCardClick(preview)}
                                                                    className="mx-5 mb-5 w-[calc(100%-2.5rem)] rounded-[20px] overflow-hidden text-left transition-all hover:scale-[1.01] active:scale-[0.99] block"
                                                                    style={{ background: "rgba(11,19,32,0.9)", border: "1px solid rgba(212,175,55,0.2)" }}
                                                                    data-testid={`message-request-share-${activeRequest.data.id}`}
                                                                >
                                                                    {preview.image_url && (
                                                                        <div className="aspect-[16/9] max-h-32 overflow-hidden bg-black/40">
                                                                            {preview.entity_type === "reel" ? (
                                                                                <video src={preview.image_url} className="w-full h-full object-cover" muted playsInline loop autoPlay />
                                                                            ) : (
                                                                                <img src={preview.image_url} alt={preview.title} className="w-full h-full object-cover" />
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    <div className="px-3.5 py-3">
                                                                        <div className="text-[9px] uppercase tracking-[0.28em] font-medium mb-1" style={{ color: "#D4AF37" }}>
                                                                            Passport {preview.entity_type}
                                                                        </div>
                                                                        <div className="text-xs font-semibold text-white line-clamp-2">{preview.title}</div>
                                                                        {preview.subtitle && <div className="text-[11px] text-white/50 mt-0.5 line-clamp-1">{preview.subtitle}</div>}
                                                                    </div>
                                                                </button>
                                                            ) : (
                                                                <div className="mx-5 mb-5 rounded-[20px] px-4 py-3 text-sm text-white/35 italic" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                                                    Loading shared content…
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>

                                                    {/* Action buttons */}
                                                    <div className="space-y-2.5">
                                                        <button
                                                            onClick={() => acceptRequest(activeRequest.data)}
                                                            className="w-full py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                                                            style={{ background: "var(--gold)", color: "var(--navy)" }}
                                                            data-testid={`request-accept-button-${activeRequest.data.id}`}
                                                        >
                                                            <CheckCircle size={16} weight="fill" /> Accept & Start Chat
                                                        </button>
                                                        <div className="grid grid-cols-3 gap-2">
                                                            <button
                                                                onClick={() => declineRequest(activeRequest.data.id)}
                                                                className="py-3 rounded-2xl text-sm flex items-center justify-center gap-1.5 transition-all"
                                                                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.65)" }}
                                                                data-testid={`request-decline-button-${activeRequest.data.id}`}
                                                            >
                                                                <X size={13} /> Decline
                                                            </button>
                                                            <button
                                                                onClick={() => blockRequestSender(activeRequest.data)}
                                                                className="py-3 rounded-2xl text-sm flex items-center justify-center gap-1.5 transition-all"
                                                                style={{ background: "rgba(244,162,143,0.07)", border: "1px solid rgba(244,162,143,0.18)", color: "#F4A28F" }}
                                                                data-testid={`request-block-button-${activeRequest.data.id}`}
                                                            >
                                                                <Warning size={13} /> Block
                                                            </button>
                                                            <button
                                                                onClick={() => setReportUserId(activeRequest.data.sender_id)}
                                                                className="py-3 rounded-2xl text-sm flex items-center justify-center gap-1.5 transition-all"
                                                                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.5)" }}
                                                                data-testid={`request-report-button-${activeRequest.data.id}`}
                                                            >
                                                                <FlagPennant size={13} /> Report
                                                            </button>
                                                        </div>
                                                    </div>
                                                </>
                                            )
                                        ) : (
                                            <div className="space-y-5">
                                                <div className="rounded-[28px] p-5" style={{ background: "rgba(159,213,208,0.1)", border: "1px solid rgba(159,213,208,0.22)" }} data-testid={`group-invite-detail-${activeRequest.data.id}`}>
                                                    <div className="text-[11px] uppercase tracking-[0.28em] text-[#9FD5D0] mb-3">Group invite</div>
                                                    <div className="font-serif text-3xl text-white">{activeRequest.data.conversation?.title || "Group chat"}</div>
                                                    <div className="text-sm text-white/55 mt-3">Join this Passport group conversation to plan, share, and chat in real time.</div>
                                                </div>
                                                <div className="grid sm:grid-cols-2 gap-3">
                                                    <button onClick={() => acceptGroupInvite(activeRequest.data)} className="btn-gold text-sm !py-3 justify-center" data-testid={`group-invite-accept-${activeRequest.data.id}`}>Accept invite</button>
                                                    <button onClick={() => declineGroupInvite(activeRequest.data)} className="btn-ghost text-sm !py-3 justify-center" data-testid={`group-invite-decline-${activeRequest.data.id}`}>Decline invite</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                                    <div className="px-4 md:px-6 py-3.5 border-b shrink-0 flex items-center justify-between gap-4" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                                        <div className="flex items-center gap-3 min-w-0">
                                            <button onClick={() => setActiveConversation(null)} className="w-9 h-9 rounded-full glass grid place-items-center lg:hidden" data-testid="messages-thread-back-button"><ArrowLeft size={15} /></button>
                                            <div className="w-10 h-10 rounded-full overflow-hidden shrink-0" style={{ background: "rgba(255,255,255,0.05)" }}>
                                                {activeConversation.type === "group" ? (
                                                    <ProtectedConversationAvatar
                                                        mediaId={activeConversation.avatar_media_id}
                                                        className="w-full h-full object-cover"
                                                        testId="active-conversation-avatar"
                                                        fallback={<div className="w-full h-full grid place-items-center text-white/70"><UsersThree size={16} /></div>}
                                                    />
                                                ) : <img src={activeConversation.other_user_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${activeConversation.other_user_handle || activeConversation.id}`} alt="" className="w-full h-full object-cover" />}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-serif text-xl md:text-2xl text-white truncate" data-testid="active-conversation-title">{activeConversation.title || activeConversation.other_user_name || "Passport chat"}</div>
                                                <div className="text-xs text-white/45 truncate">{activeConversation.type === "group" ? `${activeConversation.member_count || 0} travelers` : `@${activeConversation.other_user_handle}`}</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {activeConversation.type === "group" && <button onClick={() => setShowGroupDrawer(true)} className="w-9 h-9 rounded-full glass grid place-items-center" data-testid="chat-group-details-button"><UsersThree size={15} /></button>}
                                            <button onClick={() => setShowShareModal(true)} className="w-9 h-9 rounded-full glass grid place-items-center" data-testid="chat-share-passport-card-button"><Globe size={15} /></button>
                                        </div>
                                    </div>

                                    {(realtime.status === "offline" || realtime.status === "reconnecting" || threadNotice) && (
                                        <div className="px-5 md:px-6 py-2.5 border-b shrink-0 text-xs flex items-center gap-2" style={{ borderColor: "rgba(255,255,255,0.06)", background: threadNotice ? "rgba(244,162,143,0.1)" : "rgba(255,255,255,0.03)", color: threadNotice ? "#F4A28F" : "rgba(255,255,255,0.7)" }} data-testid="chat-reconnecting-banner">
                                            {threadNotice || (realtime.status === "offline" ? "You’re offline — Passport will reconnect when your connection returns." : "Reconnecting your live message stream…")}
                                        </div>
                                    )}

                                    <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4 space-y-4" ref={scrollRef} data-testid="messages-thread-scroll">
                                        {nextCursor && (
                                            <button onClick={loadMoreMessages} className="mx-auto block px-4 py-1.5 rounded-full text-xs border text-white/70" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }} data-testid="messages-load-older-button">
                                                <ClockCounterClockwise size={12} className="inline-block mr-2" /> Load older messages
                                            </button>
                                        )}
                                        {loadingMessages ? <div className="text-center text-white/50 py-12"><Spinner size={22} className="animate-spin inline-block" /></div> : null}
                                        {messages.map((message) => (
                                            <MessageBubble
                                                key={message.id}
                                                message={message}
                                                mine={message.sender_id === user.id}
                                                isGroup={activeConversation?.type === "group"}
                                                sharePreview={message.share_ref ? sharePreviews[`${message.share_ref.entity_type}:${message.share_ref.entity_id}`] : null}
                                                onReply={setReplyTo}
                                                onReact={reactToMessage}
                                                onDeleteForMe={deleteForMe}
                                                onUnsend={unsendMessage}
                                                onMediaClick={setActiveMediaPreview}
                                                onPassportCardClick={handlePassportCardClick}
                                            />
                                        ))}
                                        {!loadingMessages && !messages.length && (
                                            <div className="rounded-[28px] px-6 py-14 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.08)" }} data-testid="messages-thread-empty">
                                                <div className="font-serif text-2xl text-white mb-2">No messages yet.</div>
                                                <div className="text-sm text-white/45">Start the journey with your first Passport message.</div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="px-5 md:px-6 py-2 min-h-[28px] text-xs text-white/50 shrink-0" data-testid="chat-typing-indicator">
                                        {activeTyping || ""}
                                    </div>

                                    {replyTo && (
                                        <div className="px-5 md:px-6 pb-3 shrink-0" data-testid="chat-reply-banner">
                                            <div className="rounded-[22px] px-4 py-3 flex items-start justify-between gap-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">Replying to {replyTo.sender_id === user?.id ? (user?.name || "You") : (replyTo.sender_name || "traveler")}</div>
                                                    <div className="text-sm text-white/70 mt-1 line-clamp-2">{replyTo.text || replyTo.type || "Message"}</div>
                                                </div>
                                                <button onClick={() => setReplyTo(null)} className="w-8 h-8 rounded-full grid place-items-center bg-white/5" data-testid="chat-reply-cancel-button"><X size={13} /></button>
                                            </div>
                                        </div>
                                    )}

                                    {shareDraft && (
                                        <div className="px-5 md:px-6 pb-3 shrink-0" data-testid="chat-share-draft-banner">
                                            <div className="rounded-[22px] px-4 py-3 flex items-start justify-between gap-3" style={{ background: "rgba(212,175,55,0.09)", border: "1px solid rgba(212,175,55,0.18)" }}>
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">Ready to share</div>
                                                    <div className="text-sm text-white mt-1">{shareDraft.preview?.title || `${shareDraft.shareRef.entity_type} card`}</div>
                                                </div>
                                                <button onClick={() => setShareDraft(null)} className="w-8 h-8 rounded-full grid place-items-center bg-white/5" data-testid="chat-share-draft-cancel-button"><X size={13} /></button>
                                            </div>
                                        </div>
                                    )}

                                    {!!attachmentDrafts.length && (
                                        <div className="px-5 md:px-6 pb-3 shrink-0">
                                            <ComposerAttachmentDrafts drafts={attachmentDrafts} onRemove={removeDraft} onRetry={retryDraftUpload} onCancelUpload={cancelDraftUpload} />
                                        </div>
                                    )}

                                    <div className="px-4 md:px-6 pb-3.5 shrink-0">
                                        <div className="rounded-[24px] p-2.5 md:p-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                                            <div className="flex flex-col gap-2.5">
                                                <textarea
                                                    ref={textareaRef}
                                                    value={text}
                                                    onChange={(e) => handleTextChange(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" && !e.shiftKey) {
                                                            e.preventDefault();
                                                            if (!sending && composerHasContent && !threadNotice && !composerHasFailedDraft) {
                                                                sendMessage({ text, share_ref: shareDraft?.shareRef || undefined, reply_to_message_id: replyTo?.id || undefined });
                                                            }
                                                        }
                                                    }}
                                                    onBlur={stopTyping}
                                                    rows={1}
                                                    placeholder={threadNotice ? "This thread is currently locked by Passport rules." : "Write a message… (Enter to send, Shift+Enter for new line)"}
                                                    disabled={!!threadNotice}
                                                    className="w-full resize-none bg-transparent border-0 outline-none text-white text-sm placeholder:text-white/35 min-h-[26px] max-h-[110px] overflow-y-auto leading-relaxed py-1"
                                                    data-testid="chat-input-textarea"
                                                />
                                                {recording && (
                                                    <div className="rounded-[20px] px-3.5 py-2.5 flex items-center justify-between gap-3" style={{ background: "rgba(244,162,143,0.1)", border: "1px solid rgba(244,162,143,0.24)" }} data-testid="chat-recording-live-card">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-[10px] uppercase tracking-[0.24em] text-[#F4A28F]">Recording live</div>
                                                            <div className="flex items-end gap-1 h-8 mt-1.5">
                                                                {(recordingWaveform.length ? recordingWaveform : [6, 8, 10, 12, 9, 7, 11, 5]).map((value, index) => (
                                                                    <span key={`live-wave-${index}`} className="flex-1 rounded-full" style={{ height: `${Math.max(18, value * 4)}%`, background: "rgba(244,162,143,0.85)" }} />
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div className="text-xs text-[#F4A28F] shrink-0 font-mono" data-testid="chat-recording-timer">{recordingSeconds}s</div>
                                                        <button type="button" onClick={cancelVoiceMessage} className="w-8 h-8 rounded-full grid place-items-center bg-black/20 text-[#F4A28F]" data-testid="chat-recording-cancel-button"><X size={13} /></button>
                                                    </div>
                                                )}
                                                <div className="flex items-center justify-between gap-2 flex-wrap pt-0.5">
                                                    <div className="flex items-center gap-1.5">
                                                        <input ref={fileRef} type="file" className="hidden" multiple accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.json" onChange={(e) => addDraftFiles(Array.from(e.target.files || []))} data-testid="chat-attachment-input" />
                                                        <button type="button" onClick={() => fileRef.current?.click()} disabled={!activeConversation || !!threadNotice} className="w-8 h-8 rounded-full grid place-items-center bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors" data-testid="chat-attachment-button"><Paperclip size={14} /></button>
                                                        <button type="button" onClick={() => recording ? stopVoiceMessage() : startVoiceMessage()} disabled={!activeConversation || !!threadNotice} className="w-8 h-8 rounded-full grid place-items-center transition-colors" style={{ background: recording ? "rgba(244,162,143,0.16)" : "rgba(255,255,255,0.05)", color: recording ? "#F4A28F" : "rgba(255,255,255,0.7)" }} data-testid="chat-voice-record-button">
                                                            {recording ? <MicrophoneSlash size={14} /> : <Microphone size={14} />}
                                                        </button>
                                                        <button type="button" onClick={() => setShowShareModal(true)} disabled={!activeConversation || !!threadNotice} className="w-8 h-8 rounded-full grid place-items-center bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors" data-testid="chat-share-button"><ChatsCircle size={14} /></button>
                                                        <div className="text-[11px] text-white/35 hidden sm:block ml-1" data-testid="chat-composer-caption-hint">Photos, video, voice & files</div>
                                                    </div>
                                                    <button type="button" onClick={() => sendMessage({ text, share_ref: shareDraft?.shareRef || undefined, reply_to_message_id: replyTo?.id || undefined })} disabled={sending || !composerHasContent || !!threadNotice || composerHasFailedDraft} className="btn-gold text-xs !py-1.5 !px-3.5 justify-center disabled:opacity-50 font-medium" data-testid="chat-send-button">
                                                        {sending ? <Spinner size={12} className="animate-spin" /> : <PaperPlaneRight size={12} weight="fill" />} Send
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </main>
                    </div>
                </div>
            </div>

            <GroupDetailsDrawer
                open={showGroupDrawer && activeConversation?.type === "group"}
                conversation={activeConversation?.type === "group" ? activeConversation : null}
                currentUser={user}
                savedItems={savedItems}
                onClose={() => setShowGroupDrawer(false)}
                onConversationChange={syncConversation}
                onConversationLeft={handleGroupConversationExit}
                onMessageJump={handleMessageJump}
            />

            <NewConversationModal
                open={showNewModal}
                onClose={() => setShowNewModal(false)}
                initialHandle={initialComposeHandle}
                onConversationReady={async (conversation) => {
                    setShowNewModal(false);
                    await loadShell();
                    const readyConversation = conversation?.id ? conversation : { id: conversation.id };
                    await handleOpenConversation(readyConversation);
                }}
                onRequestSent={async (request) => {
                    setShowNewModal(false);
                    await loadShell();
                    setTab("requests");
                    setActiveRequest({ type: "message", data: request });
                    if (request.share_ref) ensureSharePreview(request.share_ref);
                }}
            />
            <SharePassportModal open={showShareModal} onClose={() => setShowShareModal(false)} user={user} onSelect={(shareRef, preview) => setShareDraft({ shareRef, preview })} />
            <ReportModal open={!!reportUserId} onClose={() => setReportUserId(null)} targetType="user" targetId={reportUserId} />
            <MediaLightboxModal open={Boolean(activeMediaPreview)} media={activeMediaPreview} onClose={() => setActiveMediaPreview(null)} />
            <AnimatePresence>
                {activeReelModal && (
                    <ReelModal
                        reel={activeReelModal}
                        user={user}
                        onClose={() => setActiveReelModal(null)}
                        onOpenComments={() => setCommentPost(activeReelModal)}
                        onLike={async () => {
                            try {
                                if (activeReelModal.is_post) {
                                    await postsApi.react(activeReelModal.id, "love");
                                } else {
                                    await reelsApi.like(activeReelModal.id);
                                }
                                setActiveReelModal((prev) => (prev ? { ...prev, liked: !prev.liked, likes: prev.liked ? Math.max(0, (prev.likes || 1) - 1) : (prev.likes || 0) + 1 } : null));
                            } catch {
                                toast.error("Could not update like.");
                            }
                        }}
                        onSave={async () => {
                            try {
                                if (activeReelModal.is_post) {
                                    if (activeReelModal.saved) await postsApi.unsave(activeReelModal.id);
                                    else await postsApi.save(activeReelModal.id);
                                } else {
                                    if (activeReelModal.saved) await reelsApi.unsave(activeReelModal.id);
                                    else await reelsApi.save(activeReelModal.id);
                                }
                                setActiveReelModal((prev) => (prev ? { ...prev, saved: !prev.saved } : null));
                            } catch {
                                toast.error("Could not update saved status.");
                            }
                        }}
                    />
                )}
            </AnimatePresence>
            <CommentsDrawer
                post={commentPost}
                open={Boolean(commentPost)}
                onClose={() => setCommentPost(null)}
            />
            {activeSharedDetail && (
                <SharedPassportDetailModal
                    preview={activeSharedDetail}
                    onClose={() => setActiveSharedDetail(null)}
                />
            )}
        </div>
    );
}
